import { LitElement, css, html, nothing, render } from "lit";
import { classMap } from "lit/directives/class-map.js";
import { map } from "lit/directives/map.js";
import { fa, listenForMenuClosure } from "../../utils/widgets.js";
import { faSvg } from "../styles.js";
import {
	faCircleExclamation, faCircleNotch, faMagnifyingGlass, faXmark
} from "@fortawesome/free-solid-svg-icons";
import SearchBarStyles from "./SearchBar.css" with { type: "css" };
document.adoptedStyleSheets.push(SearchBarStyles);


/**
 * Search Bar Element displays an interactive search widget.
 * @class Panoramax.components.ui.SearchBar
 * @element pnx-search-bar
 * @extends [lit.LitElement](https://lit.dev/docs/api/LitElement/)
 * @fires Panoramax.components.ui.SearchBar#result
 * @example
 * ```html
 * <pnx-search-bar
 *   id="my-search-bar"
 *   placeholder="Search something..."
 *   .searcher=${mysearchfct}
 *   ._parent=${viewer}
 *   reduceable="false"
 *   reduced="false"
 *   size="xxl" @result=${e => console.log(e.detail)}
 * >
 *   <!-- Optional icon for display on left-side of search bar -->
 * </pnx-search-bar>
 * ```
 */
export default class SearchBar extends LitElement {
	/** @private */
	static styles = [ faSvg, css`
		/* Container */
		.sb {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 5px;
			border: 1px solid var(--widget-border-div);
			border-radius: 5px;
			background-color: var(--widget-bg);
			color: var(--widget-font);
			height: 30px;
			border-radius: 20px;
			position: relative;
			padding: 0 0 0 10px;
			width: fit-content;
			max-width: 100%;
			box-sizing: border-box;
			font-family: var(--font-family);
		}
		.sb.sb-xxl {
			height: 40px;
			line-height: 40px;
		}
		.sb.sb-reduceable {
			width: 360px;
		}
		.sb.sb-reduceable.sb-reduced {
			width: fit-content;
			padding: 0;
			gap: 0;
		}

		/* Text field */
		.sb input {
			background: none;
			border: none !important;
			outline: none;
			height: 20px;
			width: 100%;
			font-family: var(--font-family);
		}
		.sb.sb-xxl input {
			font-size: 1.1em;
		}
		.sb.sb-reduceable.sb-reduced input {
			display: none;
		}

		/* Status icon */
		.sb-icon {
			cursor: pointer;
			height: 30px;
			line-height: 30px;
			width: 30px;
			min-width: 30px;
			text-align: center;
		}
		.sb.sb-xxl .sb-icon {
			height: 40px;
			line-height: 40px;
			width: 40px;
			min-width: 40px;
		}
		.sb-icon svg {
			pointer-events: none;
			width: 14px;
			height: 14px;
		}
	` ];

	/**
	 * Component properties.
	 * @memberof Panoramax.components.ui.SearchBar#
	 * @type {Object}
	 * @property {string} [id] The ID attribute set on component and prefix for input as well
	 * @property {string} [placeholder] Default text to display on empty field
	 * @property {boolean} [reduceable=false] Can the search bar be collapsed (for mobile view)
	 * @property {boolean} [reduced=false] Is the search bar currently collapsed ?
	 * @property {string} [value] The default input value
	 * @property {string} [size=md] The component sizing (md, xxl)
	 * @property {function} [searcher] Search callback, function that takes as parameter the input text value, and resolves on list of results ({title, subtitle} and any other data you'd like to get on validation)
	 * @property {boolean} [no-menu-closure=false] Set to true to not send menu closure events. Note that it still listens to other menu closures.
	 */
	static properties = {
		id: {type: String},
		placeholder: {type: String},
		reduceable: {type: Boolean, reflect: true},
		reduced: {type: Boolean, reflect: true},
		value: {type: String},
		size: {type: String},
		_icon: {state: true},
		_results: {state: true},
		searcher: {type: Function},
		"no-menu-closure": {type: Boolean},
	};

	constructor() {
		super();

		// State properties
		this.reduceable = false;
		this.reduced = false;
		this.placeholder = nothing;
		this.size = "md";
		this._icon = "search";
		this._results = null;
		this["no-menu-closure"] = false;

		// Other properties
		this._throttler = null;
		delete this._lastSearch;
	}

	/** @private */
	attributeChangedCallback(name, _old, value) {
		super.attributeChangedCallback(name, _old, value);

		if(name === "value") {
			if([null, undefined, "", "null"].includes(value)) {
				this._icon = "search";
			}
			else {
				this._icon = "empty";
			}
		}
	}

	/** @private */
	_onIconClick() {
		if(["empty", "warn"].includes(this._icon)) {
			this.reset();
		}
		if(this.reduceable && this._icon === "search") {
			this.reduced = !this.reduced;
		}
	}

	/** @private */
	_onInputChange(e) {
		if(this.value !== e.target.value) {
			this.value = e.target.value;
			this._throttledSearch();
		}
	}

	/** @private */
	_onResultClick(item) {
		let hadPrevResults = this._results !== null;
		this._results = null;
		if(hadPrevResults && !this["no-menu-closure"]) {
			this._parent?.dispatchEvent(new CustomEvent("menu-closed", { detail: { menu: this._popup }}));
		}

		if(this._throttler) {
			clearTimeout(this._throttler);
			this._throttler = null;
		}

		if(!this.reduceable && item) {
			this.value = item?.title;
			this._icon = "empty";
		}
		else {
			this.value = "";
			this._icon = "search";
			if(this.reduceable) {
				this.reduced = true;
			}
		}

		/**
		 * Event for search result clicked
		 * @event Panoramax.components.ui.SearchBar#result
		 * @type {CustomEvent}
		 * @property {object|null} detail The data associated to clicked item (format based on searcher function results), or null on reset
		 */
		this.dispatchEvent(new CustomEvent("result", {bubbles: true, composed: true, detail: item}));
	}

	/**
	 * Limit search calls to every 500ms
	 * @private
	 */
	_throttledSearch() {
		if(this._throttler) {
			clearTimeout(this._throttler);
			delete this._throttler;
		}

		this._throttler = setTimeout(this._search.bind(this), 500);
	}

	/**
	 * Perform real search
	 * @private
	 */
	_search() {
		if(!this.value || this.value.length === 0) {
			this.reset();
			return;
		}

		if(!this.searcher) {
			console.warn("No search handler defined");
			return;
		}

		this._icon = "loading";
		let hadPrevResults = this._results !== null;
		this._results = null;
		if(hadPrevResults && !this["no-menu-closure"]) {
			this._parent?.dispatchEvent(new CustomEvent("menu-closed", { detail: { menu: this._popup }}));
		}

		this.searcher(this.value).then(data => {
			if(this._icon !== "loading") { return; }

			this._icon = "empty";
			hadPrevResults = this._results !== null;
			if(!data || data.length === 0) {
				this._results = [];
			}
			else if(data === true) {
				this._results = null;
			}
			else {
				this._results = data;
			}
			if(!this["no-menu-closure"]) {
				if(!this._results && hadPrevResults) {
					this._parent?.dispatchEvent(new CustomEvent("menu-closed", { detail: { menu: this._popup }}));
				}
				else if(this._results) {
					this._parent?.dispatchEvent(new CustomEvent("menu-opened", { detail: { menu: this._popup }}));
				}
			}
		}).catch(e => {
			console.error(e);
			this._icon = "warn";
		});
	}

	/**
	 * Empty results list and reset search bar content.
	 */
	reset() {
		this._onResultClick(null);
	}

	/** @private */
	render() {
		const classes = {
			sb: true,
			"sb-xxl": this.size === "xxl",
			"sb-reduceable": this.reduceable,
			"sb-reduced": this.reduced,
		};

		const popupId = this.id + "-popup";
		this._popup = document.getElementById(popupId);
		if(!this._popup) {
			this._popup = document.createElement("div");
			this._popup.id = popupId;
			this._popup._parent = this._parent;
			this._popup.classList.add("sb-results", "pnx-panel");
			this._parent.renderRoot.appendChild(this._popup);
			listenForMenuClosure(this._popup, this.reset.bind(this));
		}

		if(!this.reduced && this._results) {
			render(html`
				${this._results.length === 0 ? html`
					<div class="sb-empty">${this._parent?._t?.pnx.search_empty}</div>
				` : nothing}

				${map(this._results, i => html`
					<div class="sb-result" @click=${() => this._onResultClick(i)}>
						${i.title}
						${i.subtitle && i.subtitle !== "" ? html`<br /><small>${i.subtitle}</small>` : nothing}
					</div>
				`)}
			`, this._popup);
			const rect = this.renderRoot.host.getBoundingClientRect();
			this._popup.style.display = "block";
			this._popup.style.top = `${rect.bottom + 5}px`;
			this._popup.style.left = `${rect.left + 5}px`;
		} else if(this._popup) {
			this._popup.style.display = "none";
		}

		return html`<div
			id=${this.id}
			class=${classMap(classes)}
			part="container"
		>
			<slot name="pre" class=${classMap({"sb-reduced": this.reduced})}></slot>

			<input
				id="${this.id}-input"
				type="text"
				placeholder=${this.placeholder}
				autocomplete="off"
				@change=${this._onInputChange.bind(this)}
				@keypress=${this._onInputChange.bind(this)}
				@paste=${this._onInputChange.bind(this)}
				@input=${this._onInputChange.bind(this)}
				.value=${this.value || ""}
				part="input"
			/>

			<span
				class="sb-icon"
				@click=${this._onIconClick.bind(this)}
			>
				${this._icon === "search" ? fa(faMagnifyingGlass) : nothing}
				${this._icon === "loading" ? fa(faCircleNotch, { classes: ["fa-spin"] }) : nothing}
				${this._icon === "empty" ? fa(faXmark) : nothing}
				${this._icon === "warn" ? fa(faCircleExclamation) : nothing}
			</span>
		</div>`;
	}
}

customElements.define("pnx-search-bar", SearchBar);
